import { wizard } from '../../modal/wizard';
import { accountStage } from './account';

/**
 * The plans door: a book, or a plan for its library, chosen in a modal.
 *
 * Any [data-lk-modal="lkRegisterModal"] opens it, and lk-modal.js does the opening.
 * This decides what is chosen when it opens: an opener naming a product with
 * data-plans-product, and a level with data-plans-access, arrives with both
 * already picked, which is how every action on the KPI Library Access page comes.
 *
 * Two things are chosen, on the same stage: the library (ld, lf) and the level
 * (book_owner, standard, premium, combo). The combo covers both libraries, so it
 * is the one level that ignores the library. What each level then asks for is the
 * second stage, and account.js owns it along with the form beneath the choices.
 */

const MODAL_ID = 'lkRegisterModal';

function plansModal() {
    const modal = document.getElementById(MODAL_ID);

    if (! modal) {
        return;
    }

    const stages = wizard(modal);
    const state = { library: '', access: '' };

    const libraries = modal.querySelector('[data-plans-libraries]');
    const levels = modal.querySelector('[data-plans-levels]');
    const continueButton = modal.querySelector('#lkPlansContinue');
    const heading = modal.querySelector('.lk-modal-head-title');
    const copyHolder = modal.querySelector('[data-plans-titles]');
    const titles = JSON.parse(copyHolder.dataset.plansTitles);
    const metaCopy = JSON.parse(copyHolder.dataset.plansMetaCopy);

    // The purchase this browser left at the checkout, when there is one.
    const resume = copyHolder.dataset.plansResume ? JSON.parse(copyHolder.dataset.plansResume) : null;

    // The stage the choices live on carries the library, which is what paints them.
    const themed = modal.querySelector('[data-stage="choose"]');
    const perYear = modal.querySelector('#registrationForm').dataset.perYear;

    const libraryOf = (value) => libraries?.querySelector(`[data-library="${value}"]`);
    const levelOf = (value) => levels?.querySelector(`[data-access="${value}"]`);

    function mark(row, attribute, value) {
        row?.querySelectorAll('.lk-plans__option').forEach((option) => {
            const chosen = option.dataset[attribute] === value;

            option.classList.toggle('is-selected', chosen);
            option.setAttribute('aria-pressed', chosen ? 'true' : 'false');
        });
    }

    function meta(name, text) {
        const line = modal.querySelector(`[data-plans-meta="${name}"]`);

        if (line) {
            // The library names itself in bold, which is why this is markup and
            // why the sentences are written in Blade rather than assembled here.
            line.innerHTML = text || '';
        }
    }

    // ── The library ──────────────────────────────────────────────────────

    function chooseLibrary(value) {
        const option = libraryOf(value);

        if (! option) {
            return;
        }

        state.library = value;
        themed.dataset.library = value;
        retitle();
        mark(libraries, 'library', value);
        meta('library', metaCopy.library[value]);

        // The book's own prices, on the levels that are sold per library.
        ['standard', 'premium'].forEach((level) => {
            const button = levelOf(level);
            const cents = Number(option.dataset[level + 'Price'] || 0);

            if (! button) {
                return;
            }

            button.dataset.price = cents;
            const price = button.querySelector(`[data-plans-level-price="${level}"]`);

            if (price) {
                price.textContent = perYear.replace(':price', '$' + (cents / 100).toLocaleString('en-US'));
            }
        });

        // The comparison table names the library it applies to.
        modal.querySelectorAll('[data-library-only]').forEach((element) => {
            element.hidden = element.dataset.libraryOnly !== value;
        });
    }

    libraries?.querySelectorAll('.lk-plans__option').forEach((option) => {
        option.addEventListener('click', () => chooseLibrary(option.dataset.library));
    });

    // ── The level ────────────────────────────────────────────────────────

    function chooseLevel(value) {
        const option = levelOf(value);

        if (! option) {
            return;
        }

        state.access = value;
        mark(levels, 'access', value);
        meta('level', metaCopy.level[value]);

        // Combo Premium is both libraries at once, so the row above stops
        // applying rather than pretending the choice still matters.
        libraries?.classList.toggle('is-idle', value === 'combo');
        retitle();
    }

    levels?.querySelectorAll('.lk-plans__option').forEach((option) => {
        option.addEventListener('click', () => chooseLevel(option.dataset.access));
    });

    // ── The comparison, for whoever asks for it ──────────────────────────

    const compareToggle = modal.querySelector('[data-plans-compare-toggle]');
    const compare = modal.querySelector('[data-plans-compare]');

    compareToggle?.addEventListener('click', () => {
        compare.hidden = ! compare.hidden;
        compareToggle.textContent = compare.hidden ? compareToggle.dataset.open : compareToggle.dataset.close;
    });

    // ── The account stage, and what it needs to know ─────────────────────

    const account = accountStage(modal, stages, {
        // The confirmation code is asked in place of the form, not on a stage of
        // its own, so the heading is all that says where the visitor is.
        onVerifying: () => { heading.textContent = titles.verify; }
    });

    function product() {
        return state.access === 'combo' ? 'combo' : state.library;
    }

    function chosen() {
        const library = libraryOf(state.library);
        const level = levelOf(state.access);
        const source = state.access === 'combo' ? level : library;

        return {
            product: product(),
            library: state.library,
            access: state.access,
            bookSlug: source?.dataset.bookSlug || '',
            bookTitle: library?.dataset.label || '',
            accessLabel: level?.dataset.label || '',
            content: source?.dataset.content || '',
            price: Number(level?.dataset.price || 0)
        };
    }

    /**
     * The button says it is working while the address is looked up.
     *
     * The round trip is short, and a button that sits still through it reads as a
     * click that missed. Held at its width so the spinner does not shrink it under
     * the cursor, the way the book's Refresh Word does.
     */
    const continueLabel = continueButton?.innerHTML;

    function working(on) {
        if (! continueButton) {
            return;
        }

        if (on) {
            continueButton.style.minWidth = `${continueButton.offsetWidth}px`;
        }

        continueButton.disabled = on;
        continueButton.innerHTML = on
            ? '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>'
            : continueLabel;

        if (! on) {
            continueButton.style.minWidth = '';
        }
    }

    continueButton?.addEventListener('click', () => {
        if (! state.access || (state.access !== 'combo' && ! state.library)) {
            return;
        }

        working(true);

        // The form is on this stage, so nothing moves on until it is filled in and
        // the address is known to be free.
        account.ready()
            .then((ready) => {
                if (! ready) {
                    return;
                }

                account.enter(chosen());
            })
            .finally(() => working(false));
    });

    // ── The heading: where the visitor is, and what they chose ───────────

    function titleFor(stage) {
        if (stage === 'done') {
            return titles.done;
        }

        if (! state.access) {
            return titles.choose;
        }

        if (state.access === 'combo') {
            return titles.combo;
        }

        const library = libraryOf(state.library);
        const level = levelOf(state.access);

        return titles.plan
            .replace(':book', library?.dataset.label || '')
            .replace(':access', level?.dataset.label || '');
    }

    const confirmTab = modal.querySelector('[data-tab="confirm"]');

    // What is on screen, said in the heading and on the tab of the stage that
    // asks for it, both of which change with the choice and not only with the step.
    function retitle() {
        heading.textContent = titleFor(stages.current());

        if (confirmTab) {
            confirmTab.textContent = state.access === 'book_owner'
                ? confirmTab.dataset.book
                : confirmTab.dataset.plan;
        }
    }

    modal.addEventListener('wizard:stage', retitle);

    // ── Opening and closing ──────────────────────────────────────────────

    document.addEventListener('click', (event) => {
        const opener = event.target.closest(`[data-lk-modal="${MODAL_ID}"]`);

        if (! opener || opener.closest('.lk-modal-backdrop')) {
            return;
        }

        const named = opener.dataset.plansProduct;

        chooseLibrary(named && named !== 'combo' ? named : state.library || libraries?.firstElementChild?.dataset.library);
        // Standard is the level an opener that names none arrives on, as the page
        // hero does; where the paid plans are off, the book is the only way in.
        const fallback = levelOf('standard') ? 'standard' : 'book_owner';

        chooseLevel(named === 'combo' ? 'combo' : (opener.dataset.plansAccess || fallback));
    });

    // Every open starts on the first stage with the choices it was opened on.
    modal.addEventListener('lk:close', () => {
        account.reset();
    });

    // ── Back from a checkout that was left without paying ────────────────

    if (resume) {
        account.resume(resume);

        // On the plan it was left at, which is what the reader came back to change.
        const named = libraries?.querySelector(`[data-book-slug="${resume.book_slug}"]`)?.dataset.library;

        chooseLibrary(named || libraries?.firstElementChild?.dataset.library);
        chooseLevel(resume.book_slug === 'combo' ? 'combo' : (resume.subscription_type || 'standard'));

        // Arriving by the checkout's own way out, the modal is the page.
        if (resume.open && window.LkModal) {
            LkModal.open(MODAL_ID);
        }
    }
}

document.addEventListener('DOMContentLoaded', plansModal);
